// src/components/admin/blog/CommentManager.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  FiMessageCircle,
  FiEdit2,
  FiTrash2,
  FiSave,
  FiX,
  FiSettings,
  FiChevronDown,
  FiChevronUp,
  FiShield,
  FiSend,
  FiCornerDownRight,
  FiCopy,
  FiCheck,
  FiThumbsUp,
  FiHeart,
  FiSmile,
  FiFrown,
  FiThumbsDown,
  FiAlertCircle,
  FiLoader,
} from 'react-icons/fi';
import { useAuth } from '../../../contexts/AuthContext';

// ============================================
// CONSTANTS & CONFIGURATION
// ============================================

const REACTIONS = {
  like: { icon: FiThumbsUp, label: 'Like', color: '#3b82f6', emoji: '👍' },
  love: { icon: FiHeart, label: 'Love', color: '#ef4444', emoji: '❤️' },
  haha: { icon: FiSmile, label: 'Haha', color: '#f59e0b', emoji: '😂' },
  sad: { icon: FiFrown, label: 'Sad', color: '#8b5cf6', emoji: '😢' },
  angry: { icon: FiThumbsDown, label: 'Angry', color: '#ef4444', emoji: '😠' },
};

const ADMIN_NAME = 'Wd Hejbul';
const ADMIN_AVATAR =
  'https://res.cloudinary.com/wd-hajbul-admin/image/upload/v1778039977/Fabicon-icon-of-wdhejbul_zrboh7.jpg';

// ============================================
// TOAST NOTIFICATION COMPONENT
// ============================================

const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 5000);
    return () => clearTimeout(timer);
  }, [onClose]);

  const styles = {
    success: 'bg-green-500 text-white',
    error: 'bg-red-500 text-white',
    warning: 'bg-yellow-500 text-white',
    info: 'bg-blue-500 text-white',
  };

  return (
    <div
      className={`fixed bottom-4 right-4 z-[10000] px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 animate-slide-in ${styles[type]}`}
    >
      {type === 'success' && <FiCheck className="w-5 h-5" />}
      {type === 'error' && <FiAlertCircle className="w-5 h-5" />}
      {type === 'warning' && <FiAlertCircle className="w-5 h-5" />}
      {type === 'info' && <FiMessageCircle className="w-5 h-5" />}
      <span className="text-sm">{message}</span>
      <button onClick={onClose} className="ml-2 hover:opacity-70">
        <FiX className="w-4 h-4" />
      </button>
    </div>
  );
};

// ============================================
// LOADING SPINNER COMPONENT
// ============================================

const LoadingSpinner = ({ size = 'md' }) => {
  const sizes = { sm: 'w-4 h-4', md: 'w-8 h-8', lg: 'w-12 h-12' };
  return (
    <div className="flex justify-center items-center">
      <div
        className={`${sizes[size]} animate-spin rounded-full border-2 border-gray-300 border-t-blue-500`}
      />
    </div>
  );
};

// ============================================
// REACTION POPUP COMPONENT
// ============================================

const ReactionPopup = ({ position, onReactionSelect, onClose, isReacting }) => {
  const popupRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = event => {
      if (popupRef.current && !popupRef.current.contains(event.target)) {
        onClose();
      }
    };
    const handleScroll = () => onClose();

    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', handleScroll);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll);
    };
  }, [onClose]);

  return (
    <div
      ref={popupRef}
      className="fixed z-[1000] bg-white dark:bg-gray-800 rounded-full shadow-xl border p-2 flex gap-1 animate-scale-in"
      style={{ top: position.top - 50, left: position.left - 120 }}
    >
      {Object.entries(REACTIONS).map(([key, reaction]) => (
        <button
          key={key}
          onClick={() => !isReacting && onReactionSelect(key)}
          disabled={isReacting}
          className="w-10 h-10 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-all hover:scale-110 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
          title={reaction.label}
        >
          {isReacting ? (
            <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
          ) : (
            <span className="text-2xl">{reaction.emoji}</span>
          )}
        </button>
      ))}
    </div>
  );
};

// ============================================
// REACTORS LIST COMPONENT
// ============================================

const ReactorsList = ({ reactors, onClose }) => {
  const [filter, setFilter] = useState('all');

  if (!reactors || reactors.length === 0) return null;

  const reactionTypes = ['all', ...new Set(reactors.map(r => r.reaction))];
  const filteredReactors =
    filter === 'all' ? reactors : reactors.filter(r => r.reaction === filter);
  const reactionCounts = {};
  reactors.forEach(r => {
    reactionCounts[r.reaction] = (reactionCounts[r.reaction] || 0) + 1;
  });

  return (
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full max-h-[80vh] overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        <div className="p-4 border-b dark:border-gray-700 flex justify-between items-center sticky top-0 bg-white dark:bg-gray-800">
          <h3 className="font-semibold">Reactions ({reactors.length})</h3>
          <button
            onClick={onClose}
            className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <FiX className="w-5 h-5" />
          </button>
        </div>

        <div className="p-3 border-b dark:border-gray-700 flex gap-2 overflow-x-auto">
          {reactionTypes.map(reaction => (
            <button
              key={reaction}
              onClick={() => setFilter(reaction)}
              className={`px-3 py-1 rounded-full text-sm transition-all whitespace-nowrap ${
                filter === reaction
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              {reaction === 'all' ? (
                'All'
              ) : (
                <span className="flex items-center gap-1">
                  <span>{REACTIONS[reaction]?.emoji}</span>
                  <span>{REACTIONS[reaction]?.label}</span>
                  <span className="text-xs ml-1">
                    ({reactionCounts[reaction]})
                  </span>
                </span>
              )}
            </button>
          ))}
        </div>

        <div className="overflow-y-auto max-h-[calc(80vh-120px)]">
          {filteredReactors.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No reactions found
            </div>
          ) : (
            filteredReactors.map((reactor, index) => (
              <div
                key={index}
                className="flex items-center gap-3 p-3 hover:bg-gray-50 dark:hover:bg-gray-700 border-b dark:border-gray-700 last:border-0"
              >
                <div className="w-10 h-10 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 flex items-center justify-center text-white font-semibold">
                  {reactor.name?.charAt(0).toUpperCase() || '?'}
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium">{reactor.name}</p>
                  <p className="text-xs text-gray-500">{reactor.email}</p>
                  <p className="text-xs text-gray-400 mt-1">
                    {new Date(reactor.createdAt).toLocaleString()}
                  </p>
                </div>
                <div className="text-2xl">
                  {REACTIONS[reactor.reaction]?.emoji}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

// ============================================
// SETTINGS POPUP COMPONENT
// ============================================

const SettingsPopup = ({ conversation, onClose, onSave }) => {
  const [maxMessages, setMaxMessages] = useState(
    conversation.settings?.maxMessages || 4,
  );
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    await onSave(conversation.id, maxMessages);
    setSaving(false);
  };

  return createPortal(
    <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div
        className="relative w-full max-w-md rounded-xl overflow-hidden"
        style={{ backgroundColor: 'var(--bg)' }}
      >
        <div
          className="flex items-center justify-between p-4 border-b"
          style={{ borderColor: 'var(--border)' }}
        >
          <h3
            className="text-lg font-semibold"
            style={{ color: 'var(--text)' }}
          >
            Conversation Settings
          </h3>
          <button
            onClick={onClose}
            className="p-1 rounded-full hover:bg-black/5 dark:hover:bg-white/5"
          >
            <FiX className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6">
          <div className="mb-4">
            <label
              className="block text-sm font-medium mb-2"
              style={{ color: 'var(--text)' }}
            >
              Max Messages Per Conversation (1-10)
            </label>
            <input
              type="number"
              min="1"
              max="10"
              value={maxMessages}
              onChange={e => setMaxMessages(parseInt(e.target.value))}
              className="w-full px-3 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500 bg-transparent"
              style={{ borderColor: 'var(--border)', color: 'var(--text)' }}
            />
            <p className="text-xs mt-1" style={{ color: 'var(--muted)' }}>
              Total messages allowed in this conversation (including user and
              admin replies)
            </p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex-1 px-4 py-2 rounded-lg bg-blue-500 text-white hover:bg-blue-600 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {saving ? <LoadingSpinner size="sm" /> : null}
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 rounded-lg border hover:bg-black/5 dark:hover:bg-white/5"
              style={{ borderColor: 'var(--border)' }}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

// ============================================
// MAIN COMMENT MANAGER COMPONENT
// ============================================

const CommentManager = ({ blogId }) => {
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingComment, setEditingComment] = useState(null);
  const [editContent, setEditContent] = useState('');
  const [settingsPopup, setSettingsPopup] = useState(null);
  const [editMaxMessages, setEditMaxMessages] = useState(4);
  const [replyTo, setReplyTo] = useState(null);
  const [replyContent, setReplyContent] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [copiedCommentId, setCopiedCommentId] = useState(null);
  const [showReactors, setShowReactors] = useState(null);
  const [reactionPosition, setReactionPosition] = useState(null);
  const [selectedCommentId, setSelectedCommentId] = useState(null);
  const [toast, setToast] = useState(null);
  const [isReacting, setIsReacting] = useState(false);
  const [pendingReactionEmoji, setPendingReactionEmoji] = useState(null);

  // CRITICAL FIX: Store reply visibility state independently for each comment
  const [replyFormsVisible, setReplyFormsVisible] = useState({});
  const [repliesVisibility, setRepliesVisibility] = useState({});

  const { getIdToken, currentUser } = useAuth();

  const ADMIN_IDENTIFIER = `admin_${currentUser?.uid || 'default'}`;

  // ============================================
  // TOAST NOTIFICATION HELPERS
  // ============================================

  const showToast = (message, type = 'info') => {
    setToast({ message, type });
  };

  // ============================================
  // AUTH TOKEN
  // ============================================

  const getAuthToken = useCallback(async () => {
    try {
      const token = await getIdToken();
      return token;
    } catch (error) {
      console.error('Failed to get auth token:', error);
      return null;
    }
  }, [getIdToken]);

  useEffect(() => {
    const markNotificationsAsRead = async () => {
      const token = await getAuthToken();
      if (!token) return;

      try {
        const response = await fetch(
          `/api/blogs?action=mark-comment-notifications-read`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ blogId }),
          },
        );

        if (response.ok) {
          console.log(`Marked notifications as read for blog ${blogId}`);
        }
      } catch (error) {
        console.error('Failed to mark notifications as read:', error);
      }
    };

    markNotificationsAsRead();
  }, [blogId, getAuthToken]);

  // ============================================
  // REACTION HANDLING WITH OPTIMISTIC UPDATE & SPINNING
  // ============================================

  const handleAdminReaction = async (commentId, reaction) => {
    const token = await getAuthToken();
    if (!token) {
      showToast('Authentication required. Please log in again.', 'error');
      return;
    }

    // Show loading indicator
    setIsReacting(true);
    setPendingReactionEmoji(REACTIONS[reaction]?.emoji);

    // Close popup immediately
    setReactionPosition(null);
    setSelectedCommentId(null);

    try {
      const response = await fetch('/api/blogs?action=react-to-comment', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          commentId,
          reaction,
          userIdentifier: ADMIN_IDENTIFIER,
          userName: ADMIN_NAME,
          userEmail: currentUser?.email || 'admin@wdhejbul.com',
        }),
      });

      const data = await response.json();
      if (data.success) {
        await loadData();
        showToast('Reaction added successfully!', 'success');
      } else {
        showToast(data.message || 'Failed to add reaction', 'error');
      }
    } catch (error) {
      console.error('Failed to add reaction:', error);
      showToast('Unable to add reaction. Please try again.', 'error');
    } finally {
      setIsReacting(false);
      setPendingReactionEmoji(null);
    }
  };

  // ============================================
  // COPY COMMENT
  // ============================================

  const handleCopyComment = async (content, commentId) => {
    try {
      await navigator.clipboard.writeText(content);
      setCopiedCommentId(commentId);
      showToast('Comment copied to clipboard!', 'success');
      setTimeout(() => setCopiedCommentId(null), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
      showToast('Unable to copy comment.', 'error');
    }
  };

  // ============================================
  // REACTION POPUP
  // ============================================

  const showReactionPopup = (event, commentId) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setReactionPosition({ top: rect.top, left: rect.left + rect.width / 2 });
    setSelectedCommentId(commentId);
  };

  const handleReactionSelect = reaction => {
    if (selectedCommentId) {
      handleAdminReaction(selectedCommentId, reaction);
    }
  };

  const closeReactionPopup = () => {
    setReactionPosition(null);
    setSelectedCommentId(null);
  };

  // ============================================
  // REPLY FORM VISIBILITY HANDLERS (Independent)
  // ============================================

  const toggleReplyForm = commentId => {
    setReplyFormsVisible(prev => ({
      ...prev,
      [commentId]: !prev[commentId],
    }));
  };

  const closeReplyForm = commentId => {
    setReplyFormsVisible(prev => ({
      ...prev,
      [commentId]: false,
    }));
  };

  // ============================================
  // REPLIES VISIBILITY HANDLERS (Independent)
  // ============================================

  const toggleRepliesVisibility = commentId => {
    setRepliesVisibility(prev => ({
      ...prev,
      [commentId]: !prev[commentId],
    }));
  };

  // ============================================
  // LOAD DATA
  // ============================================

  const loadData = useCallback(async () => {
    const token = await getAuthToken();
    if (!token) {
      setLoading(false);
      showToast('Authentication required. Please log in.', 'warning');
      return;
    }

    try {
      const response = await fetch(
        `/api/blogs?action=admin-get-comments&blogId=${blogId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      const data = await response.json();

      if (data.success) {
        const allComments = data.comments;
        const commentMap = new Map();
        const rootComments = [];

        allComments.forEach(comment => {
          comment.replies = [];
          commentMap.set(comment.id, comment);
        });

        allComments.forEach(comment => {
          if (
            comment.parentId &&
            comment.parentId !== 'root' &&
            commentMap.has(comment.parentId)
          ) {
            commentMap.get(comment.parentId).replies.push(comment);
          } else {
            rootComments.push(comment);
          }
        });

        const sortReplies = comments => {
          comments.sort(
            (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
          );
          comments.forEach(comment => {
            if (comment.replies && comment.replies.length > 0) {
              sortReplies(comment.replies);
            }
          });
        };
        sortReplies(rootComments);

        const conversationsWithSettings = await Promise.all(
          rootComments.map(async comment => {
            try {
              const settingsResponse = await fetch(
                `/api/blogs?action=get-conversation-settings&conversationId=${comment.id}`,
                {
                  headers: { Authorization: `Bearer ${token}` },
                },
              );
              const settingsData = await settingsResponse.json();
              return {
                ...comment,
                settings: settingsData.success
                  ? settingsData.settings
                  : { maxMessages: 4 },
              };
            } catch (error) {
              return { ...comment, settings: { maxMessages: 4 } };
            }
          }),
        );

        setConversations(conversationsWithSettings);
      }
    } catch (error) {
      console.error('Failed to load conversations:', error);
      showToast('Failed to load comments. Please refresh.', 'error');
    } finally {
      setLoading(false);
    }
  }, [blogId, getAuthToken]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // ============================================
  // UPDATE SETTINGS
  // ============================================

  const handleUpdateSettings = async (conversationId, maxMessages) => {
    const token = await getAuthToken();
    if (!token) {
      showToast('Authentication required. Please log in.', 'warning');
      return;
    }

    try {
      const response = await fetch(
        '/api/blogs?action=update-conversation-settings',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ conversationId, maxMessages }),
        },
      );
      const data = await response.json();
      if (data.success) {
        setSettingsPopup(null);
        await loadData();
        showToast(
          `Conversation limit updated to ${maxMessages} messages.`,
          'success',
        );
      } else {
        showToast(data.message || 'Failed to update settings', 'error');
      }
    } catch (error) {
      console.error('Failed to update settings:', error);
      showToast('Unable to update settings. Please try again.', 'error');
    }
  };

  // ============================================
  // ADMIN REPLY
  // ============================================

  const handleAdminReply = async (parentId, content) => {
    if (!content.trim()) {
      showToast('Please enter a reply message.', 'warning');
      return;
    }

    const token = await getAuthToken();
    if (!token) {
      showToast('Authentication required. Please log in.', 'warning');
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch('/api/blogs?action=admin-add-reply', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          blogId,
          parentId: parentId,
          content: content,
        }),
      });
      const data = await response.json();
      if (data.success) {
        setReplyContent('');
        closeReplyForm(parentId);
        await loadData();
        showToast('Reply posted successfully!', 'success');
      } else {
        showToast(data.message || 'Failed to add reply', 'error');
      }
    } catch (error) {
      console.error('Failed to add reply:', error);
      showToast('Unable to post reply. Please try again.', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  // ============================================
  // UPDATE COMMENT
  // ============================================

  const handleUpdateComment = async (commentId, content) => {
    if (!content.trim()) {
      showToast('Comment content cannot be empty.', 'warning');
      return;
    }

    const token = await getAuthToken();
    if (!token) {
      showToast('Authentication required. Please log in.', 'warning');
      return;
    }

    try {
      const response = await fetch('/api/blogs?action=admin-update-comment', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ commentId, content }),
      });
      const data = await response.json();
      if (data.success) {
        setEditingComment(null);
        await loadData();
        showToast('Comment updated successfully!', 'success');
      } else {
        showToast(data.message || 'Failed to update comment', 'error');
      }
    } catch (error) {
      console.error('Failed to update comment:', error);
      showToast('Unable to update comment. Please try again.', 'error');
    }
  };

  // ============================================
  // DELETE COMMENT
  // ============================================

  const handleDeleteComment = async commentId => {
    if (
      !window.confirm(
        '⚠️ Warning: This will delete the comment and ALL its replies. This action cannot be undone. Are you sure?',
      )
    ) {
      return;
    }

    const token = await getAuthToken();
    if (!token) {
      showToast('Authentication required. Please log in.', 'warning');
      return;
    }

    try {
      const response = await fetch('/api/blogs?action=admin-delete-comment', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ commentId }),
      });
      const data = await response.json();
      if (data.success) {
        await loadData();
        showToast('Comment deleted successfully!', 'success');
      } else {
        showToast(data.message || 'Failed to delete comment', 'error');
      }
    } catch (error) {
      console.error('Failed to delete comment:', error);
      showToast('Unable to delete comment. Please try again.', 'error');
    }
  };

  // ============================================
  // UTILITY FUNCTIONS
  // ============================================

  const isAdminComment = comment =>
    comment.isAdminReply || comment.authorEmail === 'admin@wdhejbul.com';

  const getAvatarColor = name => {
    const colors = [
      'bg-red-500',
      'bg-blue-500',
      'bg-green-500',
      'bg-yellow-500',
      'bg-purple-500',
      'bg-pink-500',
      'bg-indigo-500',
      'bg-teal-500',
    ];
    const index = name.length % colors.length;
    return colors[index];
  };

  const getReactionSummary = reactions => {
    if (!reactions) return null;
    const entries = Object.entries(reactions);
    if (entries.length === 0) return null;
    const topReaction = entries.reduce((a, b) => (a[1] > b[1] ? a : b));
    return {
      emoji: REACTIONS[topReaction[0]]?.emoji,
      count: entries.reduce((a, b) => a + b[1], 0),
    };
  };

  // ============================================
  // COMMENT ITEM COMPONENT
  // ============================================

  const CommentItem = ({ comment, depth = 0, isRoot = false }) => {
    const [localReplyContent, setLocalReplyContent] = useState('');
    const [isEditing, setIsEditing] = useState(false);
    const [editContentState, setEditContentState] = useState(comment.content);
    const [isSubmittingReply, setIsSubmittingReply] = useState(false);

    // Use the global state for reply form visibility
    const isReplyFormVisible = replyFormsVisible[comment.id] || false;
    const isRepliesVisible = repliesVisibility[comment.id] || false;

    const isAdminUser = isAdminComment(comment);
    const hasReplies = comment.replies && comment.replies.length > 0;
    const visibleReplies = isRepliesVisible
      ? comment.replies
      : comment.replies?.slice(0, 2) || [];
    const hasHiddenReplies = comment.replies?.length > 2;
    const reactionSummary = getReactionSummary(comment.reactions);
    const isCopied = copiedCommentId === comment.id;

    const handleLocalReply = async () => {
      if (!localReplyContent.trim()) {
        showToast('Please enter a reply message.', 'warning');
        return;
      }

      setIsSubmittingReply(true);
      await handleAdminReply(comment.id, localReplyContent);
      setLocalReplyContent('');
      setIsSubmittingReply(false);
    };

    return (
      <div className={`relative ${depth > 0 ? 'ml-10 md:ml-12' : ''}`}>
        {/* Connecting line for nested comments */}
        {depth > 0 && (
          <>
            <div
              className="absolute left-[-24px] top-8 w-[24px] h-px"
              style={{ backgroundColor: 'var(--border)' }}
            />
            <div
              className="absolute left-[-24px] top-0 bottom-8 w-px"
              style={{ backgroundColor: 'var(--border)' }}
            />
          </>
        )}

        <div className="rounded-xl p-4 transition-all duration-200 hover:bg-black/5 dark:hover:bg-white/5 group">
          <div className="flex gap-3">
            {/* Avatar */}
            <div className="flex-shrink-0">
              {isAdminUser ? (
                <img
                  src={ADMIN_AVATAR}
                  alt="Admin"
                  className="w-10 h-10 rounded-full object-cover ring-2 ring-blue-500"
                />
              ) : (
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold ${getAvatarColor(comment.authorName)}`}
                >
                  {comment.authorName.charAt(0).toUpperCase()}
                </div>
              )}
            </div>

            {/* Comment Content */}
            <div className="flex-1">
              <div className="flex items-center justify-between flex-wrap gap-2 mb-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span
                    className={`font-semibold text-sm ${isAdminUser ? 'text-blue-600 dark:text-blue-400' : ''}`}
                  >
                    {isAdminUser ? ADMIN_NAME : comment.authorName}
                  </span>
                  {isAdminUser && (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                      <FiShield className="w-3 h-3" /> Admin
                    </span>
                  )}
                  <span className="text-xs" style={{ color: 'var(--muted)' }}>
                    {comment.authorEmail}
                  </span>
                  <span className="text-xs" style={{ color: 'var(--muted)' }}>
                    {new Date(comment.createdAt).toLocaleString()}
                  </span>
                  {comment.isEdited && (
                    <span
                      className="text-xs italic"
                      style={{ color: 'var(--muted)' }}
                    >
                      (edited)
                    </span>
                  )}
                </div>

                {/* Settings Gear - Only on root comments */}
                {isRoot && (
                  <button
                    onClick={() => {
                      setSettingsPopup({
                        id: comment.id,
                        settings: comment.settings || { maxMessages: 4 },
                      });
                      setEditMaxMessages(comment.settings?.maxMessages || 4);
                    }}
                    className="p-1 rounded-full hover:bg-black/5 dark:hover:bg-white/5 transition-all"
                    title="Conversation Settings"
                  >
                    <FiSettings
                      className="w-4 h-4"
                      style={{ color: 'var(--muted)' }}
                    />
                  </button>
                )}
              </div>

              {/* Edit Mode or Normal Display */}
              {isEditing ? (
                <div className="mb-2">
                  <textarea
                    value={editContentState}
                    onChange={e => setEditContentState(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500 bg-transparent"
                    style={{
                      borderColor: 'var(--border)',
                      color: 'var(--text)',
                    }}
                    rows="3"
                  />
                  <div className="flex gap-2 mt-2">
                    <button
                      onClick={() => {
                        handleUpdateComment(comment.id, editContentState);
                        setIsEditing(false);
                      }}
                      className="px-3 py-1 rounded text-sm bg-green-500 text-white hover:bg-green-600 flex items-center gap-1"
                    >
                      <FiSave className="w-3 h-3" /> Save
                    </button>
                    <button
                      onClick={() => {
                        setEditingComment(null);
                        setIsEditing(false);
                      }}
                      className="px-3 py-1 rounded text-sm border hover:bg-black/5 dark:hover:bg-white/5"
                      style={{ borderColor: 'var(--border)' }}
                    >
                      <FiX className="w-3 h-3" /> Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <p className="text-sm mb-3" style={{ color: 'var(--text)' }}>
                  {comment.content}
                </p>
              )}

              {/* Action Buttons */}
              <div className="flex items-center gap-3 flex-wrap">
                {/* React Button */}
                <button
                  onClick={e => showReactionPopup(e, comment.id)}
                  className="text-xs hover:underline flex items-center gap-1"
                  style={{ color: 'var(--muted)' }}
                >
                  {isReacting && selectedCommentId === comment.id ? (
                    <div className="w-3 h-3 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                  ) : pendingReactionEmoji &&
                    selectedCommentId === comment.id ? (
                    <span className="text-xs">{pendingReactionEmoji}</span>
                  ) : (
                    <FiThumbsUp className="w-3 h-3" />
                  )}
                  <span>
                    {isReacting && selectedCommentId === comment.id
                      ? 'Adding...'
                      : 'React'}
                  </span>
                </button>

                {/* Reaction Summary */}
                {reactionSummary && (
                  <button
                    onClick={() => setShowReactors(comment)}
                    className="text-xs flex items-center gap-1 hover:underline"
                    style={{ color: 'var(--muted)' }}
                  >
                    <span>{reactionSummary.emoji}</span>
                    <span>{reactionSummary.count}</span>
                  </button>
                )}

                {/* Reply Button */}
                <button
                  onClick={() => toggleReplyForm(comment.id)}
                  className="text-xs hover:underline flex items-center gap-1"
                  style={{ color: 'var(--muted)' }}
                >
                  <FiCornerDownRight className="w-3 h-3" /> Reply as Admin
                </button>

                {/* Show/Hide Replies Button - Only updates when clicked */}
                {hasReplies && (
                  <button
                    onClick={() => toggleRepliesVisibility(comment.id)}
                    className="text-xs flex items-center gap-1 hover:underline"
                    style={{ color: 'var(--muted)' }}
                  >
                    {isRepliesVisible ? (
                      <FiChevronUp className="w-3 h-3" />
                    ) : (
                      <FiChevronDown className="w-3 h-3" />
                    )}
                    {isRepliesVisible
                      ? 'Hide'
                      : `Show replies (${comment.replies.length})`}
                  </button>
                )}

                {/* Edit & Delete Buttons */}
                {!isEditing && (
                  <>
                    <button
                      onClick={() => {
                        setEditingComment(comment.id);
                        setEditContentState(comment.content);
                        setIsEditing(true);
                      }}
                      className="text-xs hover:underline flex items-center gap-1"
                      style={{ color: 'var(--muted)' }}
                    >
                      <FiEdit2 className="w-3 h-3" /> Edit
                    </button>
                    <button
                      onClick={() => handleDeleteComment(comment.id)}
                      className="text-xs hover:underline flex items-center gap-1 text-red-500"
                    >
                      <FiTrash2 className="w-3 h-3" /> Delete
                    </button>
                  </>
                )}

                {/* Copy Button */}
                <button
                  onClick={() => handleCopyComment(comment.content, comment.id)}
                  className="opacity-0 group-hover:opacity-100 text-xs hover:underline flex items-center gap-1 transition-all"
                  style={{ color: 'var(--muted)' }}
                >
                  {isCopied ? (
                    <FiCheck className="w-3 h-3 text-green-500" />
                  ) : (
                    <FiCopy className="w-3 h-3" />
                  )}
                  {isCopied ? 'Copied!' : 'Copy'}
                </button>
              </div>

              {/* Reply Form - Only shows/hides via its own button */}
              {isReplyFormVisible && (
                <div className="mt-3">
                  <textarea
                    value={localReplyContent}
                    onChange={e => setLocalReplyContent(e.target.value)}
                    placeholder={`Reply as Admin to ${comment.authorName}...`}
                    className="w-full px-3 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500 bg-transparent"
                    style={{
                      borderColor: 'var(--border)',
                      color: 'var(--text)',
                    }}
                    rows="2"
                    disabled={isSubmittingReply}
                  />
                  <div className="flex gap-2 mt-2">
                    <button
                      onClick={handleLocalReply}
                      disabled={!localReplyContent.trim() || isSubmittingReply}
                      className="px-3 py-1 rounded text-sm bg-blue-500 text-white hover:bg-blue-600 disabled:opacity-50 flex items-center gap-1"
                    >
                      {isSubmittingReply ? (
                        <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <FiSend className="w-3 h-3" />
                      )}
                      {isSubmittingReply ? 'Posting...' : 'Post Reply'}
                    </button>
                    <button
                      onClick={() => closeReplyForm(comment.id)}
                      className="px-3 py-1 rounded text-sm border hover:bg-black/5 dark:hover:bg-white/5"
                      style={{ borderColor: 'var(--border)' }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Render Child Replies - Visibility controlled independently */}
        {hasReplies &&
          isRepliesVisible &&
          visibleReplies.map(reply => (
            <CommentItem
              key={reply.id}
              comment={reply}
              depth={depth + 1}
              isRoot={false}
            />
          ))}

        {/* "See More" Button for Hidden Replies */}
        {hasReplies && !isRepliesVisible && hasHiddenReplies && (
          <div className="ml-10 md:ml-12 mt-2">
            <button
              onClick={() => toggleRepliesVisibility(comment.id)}
              className="text-sm text-blue-500 hover:underline flex items-center gap-1"
            >
              <FiChevronDown className="w-4 h-4" />
              See {comment.replies.length - 2} more{' '}
              {comment.replies.length - 2 === 1 ? 'reply' : 'replies'}
            </button>
          </div>
        )}
      </div>
    );
  };

  // ============================================
  // LOADING STATE
  // ============================================

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  // ============================================
  // MAIN RENDER
  // ============================================

  return (
    <div className="space-y-6">
      {/* Toast Notifications */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      {/* Reaction Popup */}
      {reactionPosition && (
        <ReactionPopup
          position={reactionPosition}
          onReactionSelect={handleReactionSelect}
          onClose={closeReactionPopup}
          isReacting={isReacting}
        />
      )}

      {/* Reactors List Popup */}
      {showReactors && (
        <ReactorsList
          reactors={showReactors.reactors || []}
          onClose={() => setShowReactors(null)}
        />
      )}

      {/* Conversations List */}
      <div>
        <h3
          className="text-lg font-semibold mb-4 flex items-center gap-2"
          style={{ color: 'var(--text)' }}
        >
          <FiMessageCircle className="w-5 h-5" /> All Conversations (
          {conversations.length} conversations)
        </h3>

        <div className="max-h-[600px] overflow-y-auto space-y-2 pr-2 custom-scrollbar">
          {conversations.length === 0 ? (
            <div
              className="text-center py-12"
              style={{ color: 'var(--muted)' }}
            >
              <FiMessageCircle className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p className="text-lg font-medium">No comments yet</p>
              <p className="text-sm">Be the first to start a conversation!</p>
            </div>
          ) : (
            conversations.map(comment => (
              <CommentItem
                key={comment.id}
                comment={comment}
                depth={0}
                isRoot={true}
              />
            ))
          )}
        </div>
      </div>

      {/* Settings Popup */}
      {settingsPopup && (
        <SettingsPopup
          conversation={{
            id: settingsPopup.id,
            settings: settingsPopup.settings,
          }}
          onClose={() => setSettingsPopup(null)}
          onSave={handleUpdateSettings}
        />
      )}

      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: var(--border);
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: var(--muted);
          border-radius: 10px;
        }

        @keyframes scale-in {
          from {
            opacity: 0;
            transform: scale(0.8);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        @keyframes slide-in {
          from {
            opacity: 0;
            transform: translateX(100%);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        .animate-scale-in {
          animation: scale-in 0.2s ease-out forwards;
        }
        .animate-slide-in {
          animation: slide-in 0.3s ease-out forwards;
        }
      `}</style>
    </div>
  );
};

export default CommentManager;
